import React, { useCallback, useEffect, useState } from "react";
import { styled } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import {
  adminDmaPageService,
  campaignService,
  EntityService,
  myExpariencesService,
} from "../../services";
import { useSnackbar } from "notistack";
import { Container, CircularProgress, Button } from "@mui/material";
import { Typography } from "@mui/material";
import HeaderImageManager from "../../components/dragNDrop/HeaderImageManager";
import SaveIcon from "@mui/icons-material/Save";
import { FETCH_LIMIT } from "./constants";
import DragAndDropList from "./components/DragAndDropList";

const PREFIX = "AdminDmaHome";

const classes = {
  mainContainer: `${PREFIX}-mainContainer`,
  actionContainer: `${PREFIX}-actionContainer`,
  saveIconSize: `${PREFIX}-saveIconSize`,
  dndContainer: `${PREFIX}-dndContainer`,
};

const StyledDiv = styled("div")(({ theme }) => ({
  [`& .${classes.mainContainer}`]: {
    marginTop: "1rem",
    [theme.breakpoints.between("xs", "md")]: {
      marginBottom: "5rem",
    },
  },

  [`& .${classes.actionContainer}`]: {
    marginBottom: "1rem",
    textAlign: "right",
  },

  [`& .${classes.saveIconSize}`]: {
    width: "30px",
    height: "30px",
  },

  [`& .${classes.dndContainer}`]: {
    display: "flex",
    padding: "0.5rem",
    width: "50vw",
    overflow: "auto",
    "&::-webkit-scrollbar": {
      width: "5px",
      height: "5px",
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: theme.palette.primary.main,
      borderRadius: "3px",
    },
    "&::-webkit-scrollbar-track": {
      backgroundColor: "rgb(181 154 79 / 58%)",
      borderRadius: "10px",
    },
  },
}));

const initialValue = {
  data: null,
  offset: -FETCH_LIMIT,
  hasMore: true,
};

export const AdminDmaHome = () => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const [loadingUpdate, setLoadingUpdate] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [initialized, setInitialized] = useState(false);
  const [campaigns, setCampaigns] = useState(initialValue);
  const [expariences, setExpariences] = useState({
    expariences: initialValue,
    entities: initialValue,
  });
  const [currentExpariences, setCurrentExpariences] = useState({
    expariences: initialValue,
    entities: initialValue,
  });
  const [eSportExpariences, setESportExpariences] = useState(initialValue);
  const [promotedLogos, setPromotedLogos] = useState(initialValue);
  const [promotedVideos, setpromotedVideos] = useState(initialValue);
  const [ddList, setDDList] = useState(null);
  const [headersUrl, setHeadersUrl] = useState([]);

  const getCampaigns = useCallback(async () => {
    await campaignService
      // .fetchCampaigns(campaigns.offset + FETCH_LIMIT, FETCH_LIMIT)
      .fetchCampaigns(-1, -1) // set to -1 because no pagination in backend
      .then((res) => {
        setCampaigns({
          data: res,
          offset: -1,
          hasMore: false,
        });
        // use the configuration below once the backend has pagination
        // setCampaigns({
        //   data: !campaigns.data ? res.data : [...campaigns.data, ...res.data],
        //   offset: res.offset,
        //   hasMore: res.hasMore,
        // });
      })
      .catch((err) => {
        console.error(err);
        enqueueSnackbar(t("toasts.unknownError"), { variant: "error" });
      });
  }, [enqueueSnackbar, t]);
  // }, [campaigns.data, campaigns.offset, enqueueSnackbar, t]);

  const getEntitiesOrExpariences = useCallback(
    async (
      type = "expariences",
      fromExpariences = false,
      fromCurrent = false
    ) => {
      const offsetFor = fromExpariences
        ? expariences[type].offset
        : fromCurrent
        ? currentExpariences[type].offset
        : -FETCH_LIMIT;
      const myService =
        type === "entities"
          ? EntityService.getEntities(
              "sphere",
              offsetFor + FETCH_LIMIT,
              FETCH_LIMIT
            )
          : type === "expariences"
          ? myExpariencesService.fetchMyExpariences(
              offsetFor + FETCH_LIMIT,
              FETCH_LIMIT
            )
          : Promise.all([
              myExpariencesService.fetchMyExpariences(
                offsetFor + FETCH_LIMIT,
                FETCH_LIMIT
              ),
              EntityService.getEntities(
                "sphere",
                offsetFor + FETCH_LIMIT,
                FETCH_LIMIT
              ),
            ]);

      await myService
        .then((res) => {
          if (type === "all") {
            setExpariences({
              expariences: {
                data: res[0].data,
                offset: res[0].offset,
                hasMore: res[0].hasMore,
              },
              entities: {
                data: res[1].data,
                offset: res[1].offset,
                hasMore: res[1].hasMore,
              },
            });
            setCurrentExpariences({
              expariences: {
                data: res[0].data,
                offset: res[0].offset,
                hasMore: res[0].hasMore,
              },
              entities: {
                data: res[1].data,
                offset: res[1].offset,
                hasMore: res[1].hasMore,
              },
            });
          } else if (fromExpariences) {
            setExpariences({
              ...expariences,
              [type]: {
                data: !expariences[type].data
                  ? res.data
                  : [...expariences[type].data, ...res.data],
                offset: res.offset,
                hasMore: res.hasMore,
              },
            });
          } else {
            setCurrentExpariences({
              ...currentExpariences,
              [type]: {
                data: !currentExpariences[type].data
                  ? res.data
                  : [...currentExpariences[type].data, ...res.data],
                offset: res.offset,
                hasMore: res.hasMore,
              },
            });
          }
        })
        .catch((err) => {
          console.error(err);
          enqueueSnackbar(t("toasts.unknownError"), { variant: "error" });
        });
    },
    [currentExpariences, expariences, enqueueSnackbar, t]
  );

  const getPromotedLogos = useCallback(async () => {
    await myExpariencesService
      .getPromotedLogos(promotedLogos.offset + FETCH_LIMIT, FETCH_LIMIT)
      .then((res) => {
        setPromotedLogos({
          data: !promotedLogos.data
            ? res.data
            : [...promotedLogos.data, ...res.data],
          offset: res.offset,
          hasMore: res.hasMore,
        });
      })
      .catch((err) => {
        console.error(err);
        enqueueSnackbar(t("toasts.unknownError"), { variant: "error" });
      });
  }, [promotedLogos.offset, promotedLogos.data, enqueueSnackbar, t]);

  const getPromotedVideos = useCallback(async () => {
    await myExpariencesService
      .getPromotedVideos(promotedVideos.offset + FETCH_LIMIT, FETCH_LIMIT)
      .then((res) => {
        setpromotedVideos(() => ({
          data: !promotedVideos.data
            ? res.data
            : [...promotedVideos.data, ...res.data],
          offset: res.offset,
          hasMore: res.hasMore,
        }));
      })
      .catch((err) => {
        console.error(err);
        enqueueSnackbar(t("toasts.unknownError"), { variant: "error" });
      });
  }, [promotedVideos.data, promotedVideos.offset, enqueueSnackbar, t]);

  const getEsportExpariences = useCallback(async () => {
    await myExpariencesService
      .getExpariencesWithGames(
        eSportExpariences.offset + FETCH_LIMIT,
        FETCH_LIMIT
      )
      .then((res) => {
        setESportExpariences(() => ({
          data: !eSportExpariences.data
            ? res.data
            : [...eSportExpariences.data, ...res.data],
          offset: res.offset,
          hasMore: res.hasMore,
        }));
      })
      .catch((err) => {
        console.error(err);
        enqueueSnackbar(t("toasts.unknownError"), { variant: "error" });
      });
  }, [eSportExpariences.data, eSportExpariences.offset, enqueueSnackbar, t]);

  const fetchExistingData = useCallback(async () => {
    await adminDmaPageService.fetchAdminDmaPage().then((res) => {
      Object.keys(res).length === 0
        ? setDDList({})
        : setDDList({
            promotedVideos: [],
            promotedLogos: [],
            eSportExpariences: [],
            ...res,
          });
    });
  }, []);

  const fetchData = React.useCallback(() => {
    if (!fetching) {
      setFetching(true);
      Promise.all([
        getEntitiesOrExpariences("all"),
        fetchExistingData(),
        getCampaigns(),
        getPromotedLogos(),
        getPromotedVideos(),
        getEsportExpariences(),
      ])
        .then(() => {
          setInitialized(true);
          setFetching(false);
        })
        .catch((err) => {
          console.error(err);
          enqueueSnackbar(t("toasts.unknownError"), { variant: "error" });
        });
    }
  }, [
    fetching,
    getCampaigns,
    getEntitiesOrExpariences,
    getPromotedLogos,
    getPromotedVideos,
    getEsportExpariences,
    fetchExistingData,
    enqueueSnackbar,
    t,
  ]);

  useEffect(() => {
    if (!initialized) fetchData();
  }, [fetchData, initialized]);

  const handleUpdate = () => {
    let hasBeenModified = false;
    let tmpCurrentExpariences = [];
    if (ddList.currentExpariences) {
      hasBeenModified = true;
      ddList.currentExpariences.map((item) =>
        tmpCurrentExpariences.push(item._id)
      );
    }
    let tmpExpariences = [];
    if (ddList.expariences) {
      hasBeenModified = true;
      ddList.expariences.map((item) => tmpExpariences.push(item._id));
    }
    let tmpPromotedLogos = [];
    if (ddList.promotedLogos) {
      hasBeenModified = true;
      ddList.promotedLogos.map((item) => tmpPromotedLogos.push(item));
    }
    let tmpPromotedVideos = [];
    if (ddList.promotedVideos) {
      hasBeenModified = true;
      ddList.promotedVideos.map((item) => tmpPromotedVideos.push(item));
    }
    let tmpFanArtZones = [];
    if (ddList.fanartzones) {
      hasBeenModified = true;
      ddList.fanartzones.map((item) => tmpFanArtZones.push(item.id));
    }
    let tmpESportExpariences = [];
    if (ddList.eSportExpariences) {
      hasBeenModified = true;
      ddList.eSportExpariences.map((item) =>
        tmpESportExpariences.push(item.id)
      );
    }
    let tmpDdListHeader = [];
    if (ddList.headers) {
      hasBeenModified = true;
      ddList.headers.forEach((item) => {
        if (item.hasOwnProperty("file")) {
          tmpDdListHeader.push({ url: item.file.name });
        } else {
          tmpDdListHeader.push(item && item.url ? item : { url: item.url });
        }
      });
    }

    if (hasBeenModified) {
      const tmp = {
        currentExpariences: tmpCurrentExpariences,
        expariences: tmpExpariences,
        fanartzones: tmpFanArtZones,
        headers: tmpDdListHeader,
        promotedLogos: tmpPromotedLogos,
        promotedVideos: tmpPromotedVideos,
        eSportExpariences: tmpESportExpariences,
      };

      let tmpUniqueFiles = headersUrl.filter((item) =>
        item.hasOwnProperty("file")
      );
      const uniqueFiles = tmpUniqueFiles.length > 0 ? [tmpUniqueFiles[0]] : [];
      tmpUniqueFiles.forEach((file) => {
        let exist = false;
        uniqueFiles.forEach((uniq) => {
          if (uniq.name === file.name) exist = true;
        });
        if (!exist) {
          uniqueFiles.push(file);
        }
      });
      setLoadingUpdate(true);
      adminDmaPageService
        .updateAdminDmaPage(tmp, tmpUniqueFiles)
        .then(() => {
          enqueueSnackbar(t("toasts.adminPageModifiedSuccess"), {
            variant: "success",
          });
        })
        .finally(() => setLoadingUpdate(false));
    } else {
      enqueueSnackbar(t("toasts.unknownError"), { variant: "error" });
    }
  };

  const searchExparienceRelatedContent = async (
    name,
    isPromotedLogo = false,
    isPromotedVideo = false
  ) => {
    return await myExpariencesService
      .searchDmaExparience(name, isPromotedLogo, isPromotedVideo)
      .then((res) => {
        return res;
      })
      .catch((err) => {
        console.error(err);
        enqueueSnackbar(t("toasts.unknownError"), { variant: "error" });
      });
  };

  const searchEntitiesRelatedContent = async (name) => {
    return await EntityService.search(name)
      .then((res) => {
        return res;
      })
      .catch((err) => {
        console.error(err);
        enqueueSnackbar(t("toasts.unknownError"), { variant: "error" });
      });
  };

  if (!initialized || fetching) {
    return (
      <Container>
        <StyledDiv>
          <div className="loading-container">
            <CircularProgress size={50} />
            <Typography>{t("loaders.fetching")}</Typography>
          </div>
        </StyledDiv>
      </Container>
    );
  } else {
    return (
      <StyledDiv>
        <Container className={classes.mainContainer}>
          <div className={classes.actionContainer}>
            {loadingUpdate ? (
              <CircularProgress size={28} color="primary" />
            ) : (
              <Button
                disabled={loadingUpdate}
                variant="contained"
                color="primary"
                onClick={() => handleUpdate()}
              >
                <SaveIcon className={classes.saveIconSize} />
              </Button>
            )}
          </div>
          <HeaderImageManager
            id="headers"
            horizontal
            setterDDList={setDDList}
            ddList={ddList}
            setHeadersUrl={setHeadersUrl}
            sectionTitle={t("myExpariences.dmaAdminPage.headers")}
            searchApis={[
              async (name, isPromotedLogo, isPromotedVideo) =>
                await searchExparienceRelatedContent(
                  name,
                  isPromotedLogo,
                  isPromotedVideo
                ),
              async (name) => await searchEntitiesRelatedContent(name),
              (campaignType) =>
                campaigns.data.filter((c) => c.type.indexOf(campaignType) >= 0),
            ]}
          />
          <DragAndDropList
            id="expariences"
            horizontal
            logoKey="logoUrl"
            subLogoKey="logo"
            baseList={expariences}
            setBaseList={(newArr, id) =>
              setExpariences({
                ...expariences,
                [id]: { ...expariences[id], data: newArr },
              })
            }
            setterDDList={(newArr) =>
              setDDList({ ...ddList, expariences: newArr })
            }
            ddList={ddList}
            sectionTitle="Exp-AR-iences"
            baseListTitle={t("myExpariences.dmaAdminPage.ChoseSome")}
            dradOrderTitle={t("myExpariences.dmaAdminPage.Chosen")}
            getMoreData={(type, fromExp, fromCurrent) =>
              getEntitiesOrExpariences(type, fromExp, fromCurrent)
            }
            apiHasMore={expariences}
            isAsync
            searchApi={[
              async (name, isPromotedLogo, isPromotedVideo) =>
                await searchExparienceRelatedContent(
                  name,
                  isPromotedLogo,
                  isPromotedVideo
                ),
              async (name) => await searchEntitiesRelatedContent(name),
            ]}
          />
          <DragAndDropList
            id="currentExpariences"
            horizontal
            logoKey="logoUrl"
            subLogoKey="logo"
            baseList={currentExpariences}
            setBaseList={(newArr, id) =>
              setCurrentExpariences({
                ...currentExpariences,
                [id]: { ...currentExpariences[id], data: newArr },
              })
            }
            setterDDList={(newArr) =>
              setDDList({ ...ddList, currentExpariences: newArr })
            }
            ddList={ddList}
            sectionTitle={t("myExpariences.dmaAdminPage.currentExpariences")}
            baseListTitle={t("myExpariences.dmaAdminPage.ChoseSome")}
            dradOrderTitle={t("myExpariences.dmaAdminPage.Chosen")}
            getMoreData={(type, fromExp, fromCurrent) =>
              getEntitiesOrExpariences(type, fromExp, fromCurrent)
            }
            apiHasMore={currentExpariences}
            isAsync
            searchApi={[
              async (name, isPromotedLogo, isPromotedVideo) =>
                await searchExparienceRelatedContent(
                  name,
                  isPromotedLogo,
                  isPromotedVideo
                ),
              async (name) => await searchEntitiesRelatedContent(name),
            ]}
          />
          <DragAndDropList
            id="promotedLogos"
            logoKey="promotedLogoUrl"
            horizontal
            baseList={promotedLogos.data}
            setBaseList={(newArr) =>
              setPromotedLogos({ ...promotedLogos, data: newArr })
            }
            ddList={ddList}
            setterDDList={(newArr) =>
              setDDList({ ...ddList, promotedLogos: newArr })
            }
            sectionTitle={t("myExpariences.dmaAdminPage.promotedLogos")}
            baseListTitle={t("myExpariences.dmaAdminPage.ChoseSome")}
            dradOrderTitle={t("myExpariences.dmaAdminPage.Chosen")}
            getMoreData={getPromotedLogos}
            apiHasMore={promotedLogos.hasMore}
            isAsync
            searchApi={async (name, isPromotedLogo, isPromotedVideo) =>
              await searchExparienceRelatedContent(
                name,
                isPromotedLogo,
                isPromotedVideo
              )
            }
          />
          <DragAndDropList
            id="promotedVideos"
            logoKey="logoUrl"
            horizontal
            baseList={promotedVideos.data}
            setBaseList={(newArr) =>
              setpromotedVideos({ ...promotedVideos, data: newArr })
            }
            ddList={ddList}
            setterDDList={(newArr) =>
              setDDList({ ...ddList, promotedVideos: newArr })
            }
            sectionTitle={t("myExpariences.dmaAdminPage.promotedVideos")}
            baseListTitle={t("myExpariences.dmaAdminPage.ChoseSome")}
            dradOrderTitle={t("myExpariences.dmaAdminPage.Chosen")}
            getMoreData={getPromotedVideos}
            apiHasMore={promotedVideos.hasMore}
            isAsync
            searchApi={async (name, isPromotedLogo, isPromotedVideo) =>
              await searchExparienceRelatedContent(
                name,
                isPromotedLogo,
                isPromotedVideo
              )
            }
          />
          <DragAndDropList
            id="eSportExpariences"
            horizontal
            logoKey="logoUrl"
            subLogoKey="logo"
            baseList={eSportExpariences.data}
            setBaseList={(newArr) =>
              setESportExpariences({
                ...eSportExpariences,
                data: newArr,
              })
            }
            setterDDList={(newArr) =>
              setDDList({ ...ddList, eSportExpariences: newArr })
            }
            ddList={ddList}
            sectionTitle={t("myExpariences.dmaAdminPage.esportExpariences")}
            baseListTitle={t("myExpariences.dmaAdminPage.ChoseSome")}
            dradOrderTitle={t("myExpariences.dmaAdminPage.Chosen")}
            getMoreData={getEsportExpariences}
            apiHasMore={eSportExpariences.hasMore}
            isAsync
            searchApi={async (name, isPromotedLogo, isPromotedVideo) =>
              await searchExparienceRelatedContent(
                name,
                isPromotedLogo,
                isPromotedVideo
              )
            }
          />
          <DragAndDropList
            id="fanartzones"
            logoKey="logoUrl"
            horizontal
            baseList={campaigns.data}
            setBaseList={(newArr) =>
              setCampaigns({ ...campaigns, data: newArr })
            }
            ddList={ddList}
            setterDDList={(newArr) =>
              setDDList({ ...ddList, fanartzones: newArr })
            }
            sectionTitle={t("myExpariences.dmaAdminPage.campaigns")}
            baseListTitle={t("myExpariences.dmaAdminPage.ChoseSome")}
            dradOrderTitle={t("myExpariences.dmaAdminPage.Chosen")}
            uniqueKey="uniqueKey"
          />
        </Container>
      </StyledDiv>
    );
  }
};
